import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, MessageSquareText, MessageSquareReply, TrendingUp, Building2, Send, CalendarPlus } from 'lucide-react';
import { getMyGym } from '../api/gymApi';
import { getMyGymReviews, replyToReview } from '../api/reviewApi';
import { useToast } from '../components/ToastProvider';
import LineChart from '../components/LineChart';

const FETCH_LIMIT = 50;

function StarRow({ rating, size = 'w-4 h-4' }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`${size} ${i < rating ? 'fill-[#FBBC05] text-[#FBBC05]' : 'text-[#dadce0] dark:text-[#3c4043]'}`} />
      ))}
    </div>
  );
}

function formatDate(value) {
  return new Date(value).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
}

const SORTS = [
  { id: 'newest', label: 'Newest' },
  { id: 'oldest', label: 'Oldest' },
  { id: 'highest', label: 'Highest rated' },
  { id: 'lowest', label: 'Lowest rated' },
];

export default function ReviewsPage() {
  const { showToast } = useToast();
  const [gym, setGym] = useState(null);
  const [gymLoading, setGymLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [starFilter, setStarFilter] = useState(0);
  const [sort, setSort] = useState('newest');
  const [replyDraftId, setReplyDraftId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    getMyGym().then(setGym).catch(() => setGym(null)).finally(() => setGymLoading(false));
  }, []);

  useEffect(() => {
    if (gymLoading || !gym) { setLoading(false); return; }
    getMyGymReviews({ limit: FETCH_LIMIT })
      .then((data) => {
        setReviews(Array.isArray(data?.reviews) ? data.reviews : []);
        setTotal(data?.pagination?.total || 0);
      })
      .catch(() => { setReviews([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [gym, gymLoading]);

  if (gymLoading || loading) {
    return (
      <div className="flex items-center justify-center py-40">
        <div className="w-10 h-10 rounded-full border-4 border-[#e8f0fe] dark:border-[#1a2e4c] border-t-[#1a73e8] animate-spin" />
      </div>
    );
  }

  if (!gym) {
    return (
      <div className="max-w-md mx-auto text-center py-16">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-[#e8f0fe] dark:bg-[#1a2e4c] text-[#1a73e8]">
          <CalendarPlus className="w-7 h-7" />
        </div>
        <h3 className="font-semibold text-base text-[#202124] dark:text-[#e8eaed]">No gym listing yet</h3>
        <p className="text-sm text-[#5f6368] dark:text-[#9aa0a6] mt-1">Create your gym first to start collecting reviews.</p>
        <Link to="/my-gym" className="inline-block mt-4 text-sm font-semibold text-white px-5 py-2.5 rounded-full bg-[#1a73e8] hover:bg-[#1557b0]">
          Create Gym Listing
        </Link>
      </div>
    );
  }

  const avg = gym.averageRating || 0;
  const reviewCount = gym.totalReviews || reviews.length;
  const distribution = [5, 4, 3, 2, 1].map((star) => ({ star, count: reviews.filter((r) => r.rating === star).length }));
  const respondedCount = reviews.filter((r) => r.ownerReply?.text).length;
  const responseRate = reviews.length ? Math.round((respondedCount / reviews.length) * 100) : 0;

  const monthMap = {};
  [...reviews].sort((a, b) => a.createdAt.localeCompare(b.createdAt)).forEach((r) => {
    const key = r.createdAt.slice(0, 7);
    if (!monthMap[key]) monthMap[key] = { sum: 0, count: 0, label: new Date(`${key}-01`).toLocaleDateString('en-US', { month: 'short' }) };
    monthMap[key].sum += r.rating;
    monthMap[key].count += 1;
  });
  const ratingTrend = Object.values(monthMap).map((m) => ({ label: m.label, value: Number((m.sum / m.count).toFixed(1)) }));

  const filtered = reviews.filter((r) => starFilter === 0 || r.rating === starFilter).sort((a, b) => {
    if (sort === 'newest') return b.createdAt.localeCompare(a.createdAt);
    if (sort === 'oldest') return a.createdAt.localeCompare(b.createdAt);
    if (sort === 'highest') return b.rating - a.rating;
    return a.rating - b.rating;
  });

  const startReply = (review) => {
    setReplyDraftId(review._id);
    setReplyText(review.ownerReply?.text || '');
  };

  async function postReply(reviewId) {
    if (!replyText.trim()) return;
    setPosting(true);
    try {
      const updated = await replyToReview(reviewId, replyText.trim());
      setReviews((prev) => prev.map((r) => (r._id === reviewId ? updated : r)));
      setReplyDraftId(null);
      setReplyText('');
      showToast('Reply posted.');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to post reply.', 'error');
    } finally {
      setPosting(false);
    }
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-[#202124] dark:text-[#e8eaed]">Reviews & Ratings</h2>
      {total > FETCH_LIMIT && (
        <p className="text-[11px] text-[#9aa0a6]">Showing the latest {FETCH_LIMIT} of {total} reviews.</p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-8 space-y-4">
          <div className="google-card p-4 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-1.5">
              <button onClick={() => setStarFilter(0)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer ${starFilter === 0 ? 'bg-[#1a73e8] text-white' : 'bg-[#f1f3f4] dark:bg-[#202124] text-[#5f6368] dark:text-[#9aa0a6] hover:bg-[#e8eaed] dark:hover:bg-[#3c4043]'}`}>
                All ({reviews.length})
              </button>
              {distribution.map((d) => (
                <button key={d.star} onClick={() => setStarFilter(d.star)} disabled={d.count === 0}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1 ${starFilter === d.star ? 'bg-[#1a73e8] text-white' : 'bg-[#f1f3f4] dark:bg-[#202124] text-[#5f6368] dark:text-[#9aa0a6] hover:bg-[#e8eaed] dark:hover:bg-[#3c4043]'}`}>
                  {d.star} <Star className="w-3 h-3 fill-current" /> ({d.count})
                </button>
              ))}
            </div>
            <select value={sort} onChange={(e) => setSort(e.target.value)}
              className="text-xs font-semibold text-[#5f6368] dark:text-[#9aa0a6] bg-[#f1f3f4] dark:bg-[#202124] rounded-full px-3 py-1.5 outline-none cursor-pointer border-none">
              {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>

          <div className="space-y-3">
            {filtered.length === 0 ? (
              <div className="google-card p-10 text-center bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043] text-sm text-[#9aa0a6] flex flex-col items-center gap-2">
                <MessageSquareText className="w-6 h-6" />
                No reviews match this filter.
              </div>
            ) : (
              filtered.map((r) => {
                const reply = r.ownerReply?.text ? r.ownerReply : null;
                return (
                  <div key={r._id} className="google-card p-4 sm:p-5 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed]">{r.reviewerName}</h4>
                        <span className="text-[11px] text-[#9aa0a6]">{formatDate(r.createdAt)}</span>
                      </div>
                      <StarRow rating={r.rating} />
                    </div>
                    {r.comment && <p className="text-sm text-[#3c4043] dark:text-[#dadce0] mt-2.5">{r.comment}</p>}

                    {reply && replyDraftId !== r._id && (
                      <div className="mt-3 ml-4 pl-3 border-l-2 border-[#1a73e8]/30">
                        <div className="flex items-center gap-1.5 text-[11px] font-bold text-[#1a73e8] dark:text-[#8ab4f8]">
                          <Building2 className="w-3.5 h-3.5" /> Owner response · {formatDate(reply.repliedAt)}
                        </div>
                        <p className="text-xs text-[#5f6368] dark:text-[#9aa0a6] mt-1">{reply.text}</p>
                        <button onClick={() => startReply(r)} className="text-[11px] font-semibold text-[#1a73e8] dark:text-[#8ab4f8] hover:underline mt-1 cursor-pointer">
                          Edit reply
                        </button>
                      </div>
                    )}

                    {replyDraftId === r._id ? (
                      <div className="mt-3 space-y-2">
                        <div className="google-input-box px-3.5 py-2.5">
                          <textarea rows={2} value={replyText} onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Write a public reply..." maxLength={500}
                            className="w-full bg-transparent text-xs font-medium outline-none text-[#202124] dark:text-[#e8eaed] resize-none" autoFocus />
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => postReply(r._id)} disabled={posting}
                            className="px-3.5 py-1.5 rounded-full text-xs font-semibold bg-[#1a73e8] hover:bg-[#1557b0] text-white cursor-pointer flex items-center gap-1.5 disabled:opacity-60">
                            <Send className="w-3 h-3" /> {posting ? 'Posting...' : 'Post Reply'}
                          </button>
                          <button onClick={() => setReplyDraftId(null)} className="px-3.5 py-1.5 rounded-full text-xs font-semibold text-[#5f6368] dark:text-[#9aa0a6] hover:bg-[#f1f3f4] dark:hover:bg-[#202124] cursor-pointer">
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      !reply && (
                        <button onClick={() => startReply(r)} className="mt-3 text-xs font-semibold text-[#1a73e8] dark:text-[#8ab4f8] hover:underline flex items-center gap-1 cursor-pointer">
                          <MessageSquareReply className="w-3.5 h-3.5" /> Reply
                        </button>
                      )
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="lg:col-span-4 space-y-4 lg:sticky lg:top-6">
          <div className="google-card p-6 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
            <div className="text-center mb-5">
              <div className="text-5xl font-bold text-[#202124] dark:text-[#e8eaed]">{avg.toFixed(1)}</div>
              <div className="flex justify-center mt-1.5"><StarRow rating={Math.round(avg)} /></div>
              <span className="text-xs text-[#5f6368] dark:text-[#9aa0a6] mt-1 block">{reviewCount} reviews</span>
            </div>
            <div className="space-y-1.5">
              {distribution.map((d) => (
                <button key={d.star} onClick={() => setStarFilter(d.star)} className="w-full flex items-center gap-2 text-xs cursor-pointer group">
                  <span className="w-3 text-[#5f6368] dark:text-[#9aa0a6] group-hover:text-[#1a73e8]">{d.star}</span>
                  <Star className="w-3 h-3 fill-[#FBBC05] text-[#FBBC05] shrink-0" />
                  <div className="flex-1 h-1.5 rounded-full bg-[#f1f3f4] dark:bg-[#202124] overflow-hidden">
                    <div className="h-full bg-[#FBBC05] rounded-full" style={{ width: `${reviews.length ? (d.count / reviews.length) * 100 : 0}%` }} />
                  </div>
                  <span className="w-4 text-[#9aa0a6]">{d.count}</span>
                </button>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-[#e8eaed] dark:border-[#3c4043] flex items-center justify-between text-xs">
              <span className="text-[#5f6368] dark:text-[#9aa0a6]">Response rate</span>
              <span className="font-bold text-[#1a73e8] dark:text-[#8ab4f8]">{responseRate}% ({respondedCount}/{reviews.length})</span>
            </div>
          </div>

          {ratingTrend.length > 1 && (
            <div className="google-card p-5 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
              <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] mb-5 flex items-center gap-1.5">
                <TrendingUp className="w-4 h-4 text-[#1a73e8]" /> Rating Trend
              </h3>
              <LineChart data={ratingTrend} color="#FBBC05" height={120} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
